import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppContext } from '../App';
import { useUser } from '../contexts/UserContext';
import api from '../utils/api';
import logger from '../utils/logger';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../config/constants';
import { Helmet } from 'react-helmet-async';
import seoConfig from '../config/seo';

const UploadPage = () => {
  const { createNewSession } = useContext(AppContext);
  const { user, isAuthenticated, isLoading, updateUserData } = useUser();
  
  // Get allowed quality levels based on user tier
  const getAllowedQualityLevels = useMemo(() => {
    if (!user) return ['standard'];
    
    // Debug logging
    logger.debug('Checking strictness upgrade:', {
      active: user.strictnessUpgrade?.active,
      expiresAt: user.strictnessUpgrade?.expiresAt,
      isValid: user.strictnessUpgrade?.expiresAt && new Date(user.strictnessUpgrade.expiresAt) > new Date()
    });
    
    // Check if user has active strictness upgrade
    if (user.strictnessUpgrade?.active && 
        user.strictnessUpgrade?.expiresAt && 
        new Date(user.strictnessUpgrade.expiresAt) > new Date()) {
      logger.debug('User has active strictness upgrade - unlocking all levels');
      return ['standard', 'detailed', 'exhaustive'];
    }
    
    switch (user.subscriptionTier) {
      case 'free':
        return ['standard'];
      case 'starter':
        return ['standard'];
      case 'professional':
        return ['standard', 'detailed'];
      case 'business':
      case 'enterprise':
      case 'admin':
        return ['standard', 'detailed', 'exhaustive'];
      default:
        return ['standard'];
    }
  }, [user?.subscriptionTier, user?.strictnessUpgrade]);
  
  // Get max images per request based on tier
  const getMaxImagesPerRequest = useMemo(() => {
    if (!user) return 5;
    
    switch (user.subscriptionTier) {
      case 'free':
        return 5;
      case 'starter':
        return 20;
      case 'professional':
        return 50;
      case 'business':
      case 'enterprise':
      case 'admin':
        return 100;
      default:
        return 5;
    }
  }, [user?.subscriptionTier]);
  
  const allowedQualityLevels = getAllowedQualityLevels;
  const maxImagesPerRequest = getMaxImagesPerRequest;
  const [sessionName, setSessionName] = useState('');
  const [loraType, setLoraType] = useState('character');
  const [qualityLevel, setQualityLevel] = useState(allowedQualityLevels.includes('standard') ? 'standard' : allowedQualityLevels[0]);
  const [globalTags, setGlobalTags] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const navigate = useNavigate();
  
  // Refresh user data when component mounts or user changes
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      updateUserData();
    }
  }, [isAuthenticated, isLoading]);
  
  // Calculate remaining credits with fallback logic
  let used = 0;
  let limit = 10;
  
  // Check if admin (unlimited credits)
  const isAdmin = user?.subscriptionTier === 'admin';
  
  // Try to get usage from user.usage.currentPeriod first
  if (user?.usage?.currentPeriod) {
    used = user.usage.currentPeriod.imagesProcessed || 0;
    limit = user.usage.currentPeriod.monthlyLimit || user.limits?.imagesPerMonth || 10;
  } 
  // Fallback to direct limits if no usage data
  else if (user?.limits?.imagesPerMonth) {
    limit = user.limits.imagesPerMonth;
    // If no usage data exists, assume they haven't used any credits yet
    used = 0;
  }
  
  // Admin has unlimited credits
  const remaining = isAdmin || limit === -1 ? 999999 : Math.max(0, limit - used);
  const hasCredits = isAdmin || limit === -1 || remaining > 0;
  
  // Debug logging for credit calculation
  logger.debug('UploadPage - Credit calculation:', {
    email: user?.email,
    tier: user?.subscriptionTier,
    usage: user?.usage,
    limits: user?.limits,
    used,
    limit,
    remaining,
    hasCredits
  });
  
  

  // LoRA type configurations
  const loraTypes = {
    character: {
      name: 'Character',
      description: 'Physical features, expressions, clothing',
      icon: '👤',
      gradient: 'from-blue-500 to-purple-500'
    },
    style: {
      name: 'Style',
      description: 'Artistic techniques, atmosphere',
      icon: '🎨',
      gradient: 'from-pink-500 to-red-500'
    },
    object: {
      name: 'Object',
      description: 'Object details, materials, context',
      icon: '📦',
      gradient: 'from-green-500 to-teal-500'
    }
  };

  // Quality level configurations
  const qualityLevels = {
    standard: {
      name: 'Standard',
      tags: '20-30 tags',
      minTags: 10,
      dots: 2
    },
    detailed: {
      name: 'Detailed',
      tags: '30-40 tags',
      minTags: 15,
      dots: 3
    },
    exhaustive: {
      name: 'Exhaustive',
      tags: '40-50 tags',
      minTags: 20,
      dots: 4
    }
  };

  // Check authentication and load settings
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/account');
      return;
    }
    
    // Check if user is in grace period
    if (!isLoading && user && user.subscriptionStatus === 'canceled' && user.gracePeriodEnd) {
      logger.debug('User in grace period, redirecting to billing');
      navigate('/account?tab=billing');
      return;
    }
    
    // Check if free tier user has run out of credits
    // Check both 'free' and 'Free' for case sensitivity, and also check if tier is undefined/null
    const isFreeUser = !user?.subscriptionTier || user?.subscriptionTier?.toLowerCase() === 'free';
    if (!isLoading && user && isFreeUser && !hasCredits) {
      logger.debug('Redirecting free user with no credits:', user.email);
      navigate('/account?tab=billing');
      return;
    }
    
    const defaultTags = localStorage.getItem('defaultTags');
    if (defaultTags) {
      setGlobalTags(defaultTags);
    }
  }, [isAuthenticated, isLoading, navigate, user, hasCredits]); // Added hasCredits back to dependencies

  // Refresh user data when page gains focus
  useEffect(() => {
    const handleFocus = async () => {
      if (isAuthenticated && updateUserData) {
        logger.debug('Window focused - refreshing user data');
        await updateUserData();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    // Also refresh when component mounts if authenticated
    if (isAuthenticated && updateUserData) {
      logger.debug('Component mounted - refreshing user data');
      updateUserData();
    }
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated]); // Remove updateUserData from dependencies to prevent loops

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      // Revoke all object URLs to free memory
      Object.values(filePreviewUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [filePreviewUrls]);

  // Create preview URLs for image files
  const createPreviewUrls = (files) => {
    const newUrls = {};
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        newUrls[index] = url;
      }
    });
    return newUrls;
  };

  // Validate file before adding
  const validateFile = (file) => {
    const errors = [];
    
    // Check if file is empty (0 bytes)
    if (!file.size || file.size === 0) {
      errors.push(`"${file.name}" is empty (0 bytes)`);
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      errors.push(`"${file.name}" is too large (${sizeMB}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB`);
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      errors.push(`"${file.name}" is not an image file`);
    }
    
    // Check specific image types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      errors.push(`"${file.name}" has unsupported image format. Supported: JPEG, PNG, GIF, WebP, BMP`);
    }
    
    return errors;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const allErrors = [];
    
    // Validate each file
    files.forEach(file => {
      const errors = validateFile(file);
      if (errors.length > 0) {
        allErrors.push(...errors);
      } else {
        validFiles.push(file);
      }
    });
    
    // Show validation errors
    if (allErrors.length > 0) {
      allErrors.forEach(error => toast.error(error));
    }
    
    // If no valid files, reset and return
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    const currentLength = selectedFiles.length;
    
    // Check if adding these files would exceed the limit
    if (!isAdmin && currentLength + validFiles.length > maxImagesPerRequest) {
      const allowedToAdd = maxImagesPerRequest - currentLength;
      if (allowedToAdd <= 0) {
        toast.error(`Maximum ${maxImagesPerRequest} images allowed per request`);
        e.target.value = ''; // Reset file input
        return;
      } else {
        toast.warning(`Can only add ${allowedToAdd} more images. Limit is ${maxImagesPerRequest} per request.`);
        validFiles.splice(allowedToAdd); // Keep only allowed number of files
      }
    }
    
    const newFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(newFiles);
    
    // Create preview URLs for new files
    const newPreviewUrls = createPreviewUrls(validFiles);
    const adjustedUrls = {};
    Object.entries(newPreviewUrls).forEach(([key, url]) => {
      adjustedUrls[parseInt(key) + currentLength] = url;
    });
    setFilePreviewUrls(prev => ({ ...prev, ...adjustedUrls }));
    
    // Reset file input
    e.target.value = '';
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const validFiles = [];
    const allErrors = [];
    
    // Validate each file
    files.forEach(file => {
      const errors = validateFile(file);
      if (errors.length > 0) {
        allErrors.push(...errors);
      } else {
        validFiles.push(file);
      }
    });
    
    // Show validation errors
    if (allErrors.length > 0) {
      allErrors.forEach(error => toast.error(error));
    }
    
    // If no valid files, return
    if (validFiles.length === 0) {
      return;
    }
    
    const currentLength = selectedFiles.length;
    
    // Check if adding these files would exceed the limit
    if (!isAdmin && currentLength + validFiles.length > maxImagesPerRequest) {
      const allowedToAdd = maxImagesPerRequest - currentLength;
      if (allowedToAdd <= 0) {
        toast.error(`Maximum ${maxImagesPerRequest} images allowed per request`);
        return;
      } else {
        toast.warning(`Can only add ${allowedToAdd} more images. Limit is ${maxImagesPerRequest} per request.`);
        validFiles.splice(allowedToAdd); // Keep only allowed number of files
      }
    }
    
    const newFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(newFiles);
    
    // Create preview URLs for new files
    const newPreviewUrls = createPreviewUrls(validFiles);
    const adjustedUrls = {};
    Object.entries(newPreviewUrls).forEach(([key, url]) => {
      adjustedUrls[parseInt(key) + currentLength] = url;
    });
    setFilePreviewUrls(prev => ({ ...prev, ...adjustedUrls }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Remove file
  const removeFile = (index) => {
    // Revoke the preview URL to free memory
    if (filePreviewUrls[index]) {
      URL.revokeObjectURL(filePreviewUrls[index]);
    }
    
    // Remove the file
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Update preview URLs - rebuild the mapping
    setFilePreviewUrls(prev => {
      const newUrls = {};
      Object.entries(prev).forEach(([key, url]) => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newUrls[keyIndex] = url;
        } else if (keyIndex > index) {
          newUrls[keyIndex - 1] = url;
        }
      });
      return newUrls;
    });
  };

  // Upload files
  const uploadFiles = async (sessionId) => {
    setIsUploading(true);
    
    try {
      // Final validation before upload
      const invalidFiles = [];
      selectedFiles.forEach(file => {
        const errors = validateFile(file);
        if (errors.length > 0) {
          invalidFiles.push({ file: file.name, errors });
        }
      });
      
      if (invalidFiles.length > 0) {
        const errorMessage = invalidFiles.map(f => `${f.file}: ${f.errors.join(', ')}`).join('\n');
        throw new Error(`Invalid files detected:\n${errorMessage}`);
      }
      
      const result = await api.upload.multiple(selectedFiles, sessionId);
      logger.debug('Upload successful:', result);
      
      // Verify upload was successful
      if (!result || (!result.data && !result.files)) {
        throw new Error('Upload completed but no data returned');
      }
      
      // Handle response structure (might be wrapped in data or direct)
      const uploadData = result.data || result;
      
      // Check for failed files
      if (uploadData.failedFiles && uploadData.failedFiles.length > 0) {
        // Show errors for each failed file
        uploadData.failedFiles.forEach(failed => {
          toast.error(`${failed.filename}: ${failed.error}`, { duration: 8000 });
        });
        
        // If all files failed, throw an error
        if (uploadData.summary && uploadData.summary.successful === 0) {
          throw new Error('All files failed validation. Please check your images and try again.');
        }
        
        // Show warning about partial success
        if (uploadData.summary) {
          toast.warning(`${uploadData.summary.failed} files failed validation. ${uploadData.summary.successful} files uploaded successfully.`, { duration: 8000 });
        }
      }
      
      // Show notification if filenames were sanitized
      if (uploadData.filenameNote) {
        toast.info(uploadData.filenameNote, { duration: 5000 });
      }
      
      return uploadData;
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload images: ' + error.message);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Generate captions
  const generateCaptions = async (sessionId) => {
    setIsProcessing(true);
    setProcessingProgress({ current: 0, total: selectedFiles.length });

    try {
      const result = await api.caption.generate({
        sessionId,
        globalTags,
        template: loraType,
        strictness: qualityLevel,
        imageCount: selectedFiles.length
      });
      
      // Check for failed files in the results
      if (result.results) {
        const failedFiles = result.results.filter(r => r.status === 'failed');
        if (failedFiles.length > 0) {
          // Show errors for failed files
          failedFiles.forEach(failed => {
            toast.error(`Failed: ${failed.file} - ${failed.error}`, { duration: 10000 });
          });
          
          // Show summary if some failed
          if (result.summary) {
            const { successful, failed } = result.summary;
            if (failed > 0) {
              toast.warning(`Caption generation completed with errors: ${successful} successful, ${failed} failed`, { duration: 10000 });
            }
          }
        }
      }
      
      // Small delay to ensure files are written
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh user data to update credits
      await updateUserData();
      
      // Clear files and previews before navigation to avoid blob URL errors
      setSelectedFiles([]);
      setFilePreviewUrls({});
      
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to session details
      navigate(`/session/${sessionId}`);
      
    } catch (error) {
      logger.error('Caption generation error:', error);
      toast.error('Failed to generate captions: ' + error.message);
      setCurrentSessionId(null); // Clear session ID on error
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancellation
  const handleCancel = async () => {
    setIsCancelling(true);
    
    try {
      // If we have a session ID, delete it to stop processing
      if (currentSessionId) {
        try {
          await api.sessions.delete(currentSessionId);
          logger.debug('Session deleted successfully');
        } catch (error) {
          logger.error('Error deleting session:', error);
        }
      }
      
      // Reset all states
      setIsUploading(false);
      setIsProcessing(false);
      setProcessingProgress({ current: 0, total: 0 });
      setCurrentSessionId(null);
      
      // Clear files and previews
      Object.values(filePreviewUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
      setFilePreviewUrls({});
      setSelectedFiles([]);
      
      toast.info('Operation cancelled');
    } finally {
      setIsCancelling(false);
    }
  };

  // Start processing
  // Handle strictness upgrade
  const handleStrictnessUpgrade = async () => {
    logger.debug('Strictness upgrade clicked');
    try {
      const response = await api.billing.createStrictnessUpgradeSession();
      logger.debug('Strictness upgrade response:', response);
      
      // Check both response.url and response.data.url for the checkout URL
      const checkoutUrl = response.url || response.data?.url;
      
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        logger.error('No checkout URL in response:', response);
        toast.error('No checkout URL received');
      }
    } catch (error) {
      logger.error('Strictness upgrade error:', error);
      toast.error(error.message || 'Failed to start upgrade process');
    }
  };

  const startProcessing = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    // Check if user has enough credits
    if (!hasCredits) {
      if (user?.subscriptionTier === 'free') {
        toast.error('No credits remaining. Please upgrade to continue.');
        navigate('/account?tab=billing');
      } else {
        toast.error('No credits remaining. Please purchase more credits.');
        navigate('/account?tab=billing');
      }
      return;
    }

    // Check if user has enough credits for the selected images
    if (selectedFiles.length > remaining) {
      toast.error(`You only have ${remaining} credits remaining but selected ${selectedFiles.length} images.`);
      return;
    }
    
    // Check if user exceeds per-request limit
    if (selectedFiles.length > maxImagesPerRequest) {
      const tierName = user?.subscriptionTier === 'free' ? 'Free' : 
                      user?.subscriptionTier === 'starter' ? 'Starter' :
                      user?.subscriptionTier === 'professional' ? 'Professional' :
                      user?.subscriptionTier === 'business' ? 'Business' : 'Your';
      toast.error(`${tierName} tier is limited to ${maxImagesPerRequest} images per request. You selected ${selectedFiles.length} images.`);
      return;
    }

    try {
      // Create new session
      const session = await createNewSession(
        sessionName || `LoRA Training ${new Date().toLocaleDateString()}`,
        {
          loraType,
          qualityLevel,
          globalTags,
          imageCount: selectedFiles.length
        }
      );
      
      if (!session || !session.id) {
        throw new Error('Failed to create session');
      }

      setCurrentSessionId(session.id);

      // Upload files
      const uploadResult = await uploadFiles(session.id);
      
      // Small delay to ensure files are processed
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate captions
      await generateCaptions(session.id);
      
    } catch (error) {
      logger.error('Processing error:', error);
    }
  };

  // Poll for progress
  useEffect(() => {
    let interval;
    
    if (isProcessing && currentSessionId && !isCancelling) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(`/api/caption/status/${currentSessionId}`);
          if (response.ok) {
            const status = await response.json();
            setProcessingProgress({
              current: status.processedImages || 0,
              total: status.totalImages || selectedFiles.length
            });
            
            // If processing is complete, clean up
            if (status.processedImages >= status.totalImages && status.totalImages > 0) {
              // Clean up all preview URLs
              Object.values(filePreviewUrls).forEach(url => {
                URL.revokeObjectURL(url);
              });
              setFilePreviewUrls({});
              setSelectedFiles([]);
              setIsProcessing(false);
              setCurrentSessionId(null); // Clear session ID to stop polling
            }
          } else if (response.status === 404) {
            // Session was deleted (cancelled)
            logger.debug('Session not found, likely cancelled');
            return;
          }
        } catch (error) {
          logger.error('Error fetching progress:', error);
        }
      }, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, currentSessionId, selectedFiles.length, isCancelling]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto"></div>
          <p className="mt-4 text-secondaryText">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Check if account is locked
  if (user && user.subscriptionStatus === 'locked') {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="card bg-red-500/10 border border-red-500/30 p-8 text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-primaryText mb-2">Account Locked</h2>
          <p className="text-secondaryText mb-4">
            Your account has been locked due to a failed payment. You have {user.preservedCredits || 0} credits preserved, 
            but you cannot use them until you update your payment method.
          </p>
          <p className="text-sm text-secondaryText mb-6">
            Grace period ends: {user.gracePeriodEnd ? new Date(user.gracePeriodEnd).toLocaleDateString() : 'N/A'}
          </p>
          <Link 
            to="/account?tab=billing" 
            className="inline-flex items-center gap-2 bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Update Payment Method
          </Link>
        </div>
      </div>
    );
  }
  
  // Prevent rendering if free tier user has no credits (redirect will happen in useEffect)
  const isFreeUser = !user?.subscriptionTier || user?.subscriptionTier?.toLowerCase() === 'free';
  if (user && isFreeUser && !hasCredits) {
    // Double-check and force redirect
    logger.debug('UploadPage - Redirecting free user with no credits');
    navigate('/account?tab=billing', { replace: true });
    // Force a page reload to ensure fresh data
    window.location.href = '/account?tab=billing';
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto"></div>
          <p className="mt-4 text-secondaryText">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Processing view
  if (isUploading || isProcessing) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm animate-fade-in">
        <div className="bg-surface rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            {isUploading ? (
              <>
                <div className="mb-6 relative w-40 h-40 mx-auto">
                  {/* Green box in center */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-accent to-accent/70 rounded-2xl flex items-center justify-center shadow-lg animate-pulse z-10">
                    <span className="text-3xl text-white">📤</span>
                  </div>
                  {/* Pulsating stars around the box */}
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg animate-pulse-slow" style={{animationDelay: '0s'}}>✨</span>
                  <span className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 text-sm animate-pulse-slow" style={{animationDelay: '0.5s'}}>⭐</span>
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-lg animate-pulse-slow" style={{animationDelay: '1s'}}>✨</span>
                  <span className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 text-sm animate-pulse-slow" style={{animationDelay: '1.5s'}}>⭐</span>
                  <span className="absolute top-2 right-8 text-xs animate-pulse-slow" style={{animationDelay: '2s'}}>✨</span>
                  <span className="absolute bottom-8 right-2 text-xs animate-pulse-slow" style={{animationDelay: '2.5s'}}>⭐</span>
                  <span className="absolute top-8 left-2 text-xs animate-pulse-slow" style={{animationDelay: '0.3s'}}>✨</span>
                  <span className="absolute bottom-2 left-8 text-xs animate-pulse-slow" style={{animationDelay: '1.8s'}}>⭐</span>
                </div>
                <h2 className="text-2xl font-medium text-primaryText mb-3">Uploading images...</h2>
                <p className="text-secondaryText">Please wait while we upload your files</p>
              </>
            ) : (
              <>
                <div className="mb-6 relative w-40 h-40 mx-auto">
                  {/* Green box in center */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-accent to-accent/70 rounded-2xl flex items-center justify-center shadow-lg animate-pulse-slow z-10">
                    <span className="text-3xl text-white">✨</span>
                  </div>
                  {/* Pulsating stars around the box */}
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg animate-pulse-slow" style={{animationDelay: '0s'}}>✨</span>
                  <span className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 text-sm animate-pulse-slow" style={{animationDelay: '0.5s'}}>⭐</span>
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-lg animate-pulse-slow" style={{animationDelay: '1s'}}>✨</span>
                  <span className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 text-sm animate-pulse-slow" style={{animationDelay: '1.5s'}}>⭐</span>
                  <span className="absolute top-2 right-8 text-xs animate-pulse-slow" style={{animationDelay: '2s'}}>✨</span>
                  <span className="absolute bottom-8 right-2 text-xs animate-pulse-slow" style={{animationDelay: '2.5s'}}>⭐</span>
                  <span className="absolute top-8 left-2 text-xs animate-pulse-slow" style={{animationDelay: '0.3s'}}>✨</span>
                  <span className="absolute bottom-2 left-8 text-xs animate-pulse-slow" style={{animationDelay: '1.8s'}}>⭐</span>
                </div>
                <h2 className="text-2xl font-medium text-primaryText mb-3">Generating captions...</h2>
                <p className="text-secondaryText mb-2">
                  Processing {processingProgress.current} of {processingProgress.total} images
                </p>
                <p className="text-sm text-secondaryText/70 mb-8">
                  Please be patient - this may take a few minutes depending on the number of images
                </p>
                
                {/* Progress bar */}
                <div className="w-full bg-button rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-accent to-accent/80 h-3 rounded-full transition-all duration-500 shadow-inner"
                    style={{ 
                      width: `${processingProgress.total > 0 
                        ? (processingProgress.current / processingProgress.total) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </>
            )}
          </div>
          
          {/* Cancel button */}
          <button
            onClick={handleCancel}
            disabled={isCancelling}
            className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all ${
              isCancelling 
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                : 'bg-red-500/20 text-red-500 hover:bg-red-500/30 border border-red-500/30'
            }`}
          >
            {isCancelling ? (
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Cancelling...
              </span>
            ) : (
              'Cancel'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in px-4">
      <Helmet>
        <title>{seoConfig.upload.title}</title>
        <meta name="description" content={seoConfig.upload.description} />
      </Helmet>
      {/* Credits warning for low/no credits */}
      {isAuthenticated && !isAdmin && remaining <= 5 && (
        <div className={`mb-4 p-4 rounded-lg border ${
          hasCredits 
            ? 'bg-yellow-500/10 border-yellow-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-start">
              <svg className={`w-5 h-5 ${hasCredits ? 'text-yellow-500' : 'text-red-500'} mt-0.5 mr-3 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-primaryText">
                  {hasCredits ? `Only ${remaining} credits remaining` : 'No credits remaining'}
                </h3>
                <p className="text-sm text-secondaryText">
                  {user?.subscriptionTier === 'free' 
                    ? 'Upgrade to a paid plan for more credits and premium features.' 
                    : 'Purchase additional credits to continue processing images.'}
                </p>
              </div>
            </div>
            {user?.subscriptionTier === 'free' ? (
              <Link to="/account?tab=billing" className="btn-primary text-sm py-1.5 px-4 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Upgrade
              </Link>
            ) : (
              <button 
                onClick={() => navigate('/account?tab=billing')}
                className="btn-primary text-sm py-1.5 px-4 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Top Up
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:h-[calc(100vh-8rem)] overflow-hidden">
        {/* Left side - Settings */}
        <div className="flex flex-col gap-3 animate-slide-up lg:h-full overflow-hidden" style={{ animationDelay: '100ms' }}>
          {/* LoRA Type */}
          <div className="card">
            <h3 className="text-sm sm:text-base font-semibold text-primaryText mb-2">LoRA Type</h3>
            <div className="space-y-1">
              {Object.entries(loraTypes).map(([key, config]) => (
                <label
                  key={key}
                  className={`relative flex items-center p-3 sm:p-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    loraType === key
                      ? 'bg-accent/10 border-2 border-accent shadow-card'
                      : 'border-2 border-border hover:border-border/80 hover:bg-surface'
                  }`}
                >
                  <input
                    type="radio"
                    name="loraType"
                    value={key}
                    checked={loraType === key}
                    onChange={(e) => setLoraType(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-base sm:text-lg mr-3">{config.icon}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primaryText">{config.name}</p>
                    <p className="text-xs text-secondaryText">{config.description}</p>
                  </div>
                  {loraType === key && (
                    <div className="absolute top-3 right-3 w-2 h-2 bg-accent rounded-full"></div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Tag Quality Level */}
          <div className="card lg:flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mb-2">
              <h3 className="text-sm sm:text-base font-semibold text-primaryText">Tag Quality</h3>
              {!isAdmin && !allowedQualityLevels.includes('exhaustive') && user?.subscriptionTier !== 'free' && (
                <button 
                  onClick={handleStrictnessUpgrade}
                  className="text-xs text-accent hover:text-accent/80 font-medium transition-colors underline"
                >
                  Upgrade to Exhaustive for $1.99
                </button>
              )}
            </div>
            <div className="space-y-3 lg:flex-1 overflow-y-auto">
              {Object.entries(qualityLevels).map(([key, config]) => {
                const isAllowed = allowedQualityLevels.includes(key);
                const isDisabled = !isAllowed;
                
                return (
                  <label
                    key={key}
                    className={`relative flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${
                      isDisabled 
                        ? 'opacity-50 cursor-not-allowed border-2 border-border/50' 
                        : qualityLevel === key
                          ? 'bg-accent/10 border-2 border-accent shadow-card cursor-pointer'
                          : 'border-2 border-border hover:border-border/80 hover:bg-surface cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="qualityLevel"
                      value={key}
                      checked={qualityLevel === key}
                      onChange={(e) => !isDisabled && setQualityLevel(e.target.value)}
                      disabled={isDisabled}
                      className="sr-only"
                    />
                    <div className="flex-1 mr-2">
                      <p className={`text-sm font-medium ${isDisabled ? 'text-gray-500' : 'text-primaryText'}`}>
                        {config.name}
                        {isDisabled && <span className="text-xs text-red-400 ml-1 block sm:inline">(Upgrade required)</span>}
                      </p>
                      <p className={`text-xs ${isDisabled ? 'text-gray-600' : 'text-secondaryText'}`}>{config.tags}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            isDisabled ? 'bg-gray-600' : i < config.dots ? 'bg-accent' : 'bg-border'
                          }`}
                        />
                      ))}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

        </div>

        {/* Right side - Upload area */}
        <div className="lg:col-span-2 animate-slide-up lg:h-full overflow-hidden" style={{ animationDelay: '200ms' }}>
          <div className="card lg:h-full flex flex-col overflow-hidden min-h-[400px]">
            {/* Credits Counter - inside upload panel */}
            {!isAdmin && (
              <div className="bg-surface rounded-lg p-4 mb-4 border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-accent mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-xs text-secondaryText">Credits Remaining</p>
                      <p className="text-lg font-semibold text-primaryText">
                        {remaining.toLocaleString()}
                        <span className="text-xs font-normal text-secondaryText ml-1">/ {limit === -1 ? '∞' : limit.toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Progress bar */}
                    <div className="w-32 bg-button rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          (remaining / limit) > 0.5 ? 'bg-accent' : 
                          (remaining / limit) > 0.2 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${limit === -1 || limit === 0 ? 100 : Math.min(100, (remaining / limit) * 100)}%` }}
                      />
                    </div>
                    {remaining < 100 && user?.subscriptionTier !== 'free' && (
                      <button 
                        onClick={() => navigate('/account?tab=billing')}
                        className="text-xs text-accent hover:text-accent/80 font-medium"
                      >
                        Top up
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Session name and Global Tags */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 sm:mb-6">
              <div>
                <label htmlFor="session-name" className="block text-xs sm:text-sm font-medium text-primaryText mb-1 sm:mb-2">
                  Session Name <span className="text-secondaryText font-normal">(optional)</span>
                </label>
                <input
                  id="session-name"
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="My LoRA training session"
                  className="w-full text-sm sm:text-base"
                />
              </div>
              <div>
                <label htmlFor="global-tags" className="block text-xs sm:text-sm font-medium text-primaryText mb-1 sm:mb-2">
                  Global Tags <span className="text-secondaryText font-normal">(optional)</span>
                </label>
                <input
                  id="global-tags"
                  type="text"
                  value={globalTags}
                  onChange={(e) => setGlobalTags(e.target.value)}
                  placeholder="Tags to add to all captions"
                  className="w-full text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 flex-1 flex flex-col justify-center overflow-hidden ${
                isDragging
                  ? 'border-accent bg-accent/5 shadow-lg'
                  : 'border-border hover:border-accent/50 hover:bg-surface'
              }`}
            >
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFiles.length === 0 ? (
                <>
                  {/* Background image that extends 2px beyond container */}
                  <div 
                    className="absolute -inset-[2px] rounded-2xl"
                    style={{
                      backgroundImage: 'url("https://picsum.photos/800/600?grayscale")',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                  
                  {/* Semi-transparent overlay for better text readability */}
                  <div className="absolute -inset-[2px] bg-background/80 rounded-2xl" />
                  
                  <label htmlFor="file-upload" className="cursor-pointer block relative z-10">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-button rounded-xl mx-auto mb-3 sm:mb-4 flex items-center justify-center shadow-button transition-all hover:shadow-lg hover:scale-105">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-secondaryText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-base sm:text-lg font-medium text-primaryText mb-1 sm:mb-2">Drop images here or tap to browse</p>
                    <p className="text-xs sm:text-sm text-secondaryText mb-1">Supports PNG, JPG, WebP up to 20MB each</p>
                    <p className="text-xs sm:text-sm text-accent font-medium">
                      {isAdmin ? (
                        'Unlimited images per request'
                      ) : (
                        `Maximum ${maxImagesPerRequest} images per request`
                      )}
                    </p>
                  </label>
                </>
              ) : (
                <div className="lg:absolute lg:inset-0 lg:p-6 flex flex-col h-full">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 flex-shrink-0 gap-2">
                    <div>
                      <h4 className="text-base sm:text-lg font-medium text-primaryText">
                        {selectedFiles.length} {selectedFiles.length === 1 ? 'image' : 'images'} selected
                      </h4>
                      <p className={`text-xs mt-1 ${
                        selectedFiles.length > maxImagesPerRequest 
                          ? 'text-red-500 font-medium' 
                          : selectedFiles.length === maxImagesPerRequest 
                            ? 'text-yellow-500 font-medium'
                            : 'text-secondaryText'
                      }`}>
                        {isAdmin ? (
                          'Unlimited images allowed'
                        ) : selectedFiles.length > maxImagesPerRequest ? (
                          `Exceeds limit of ${maxImagesPerRequest} images`
                        ) : (
                          `${maxImagesPerRequest - selectedFiles.length} more allowed`
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          // Revoke all preview URLs to free memory
                          Object.values(filePreviewUrls).forEach(url => {
                            URL.revokeObjectURL(url);
                          });
                          setFilePreviewUrls({});
                          setSelectedFiles([]);
                        }}
                        className="text-red-500 hover:text-red-400 font-medium transition-colors text-sm"
                      >
                        Clear All
                      </button>
                      <span className="text-gray-500">|</span>
                      <label htmlFor="file-upload" className={`cursor-pointer font-medium transition-colors ${
                        selectedFiles.length >= maxImagesPerRequest && !isAdmin
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-accent hover:text-accent/80'
                      }`}>
                        + Add more
                      </label>
                    </div>
                  </div>
                  
                  {/* File list */}
                  <div className="space-y-2 flex-1 overflow-y-auto pr-2 custom-scrollbar max-h-[300px] lg:max-h-none">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-lg hover:bg-button transition-colors group">
                        <div className="flex items-center flex-1 mr-3">
                          {filePreviewUrls[index] ? (
                            <img 
                              src={filePreviewUrls[index]} 
                              alt={file.name}
                              className="w-12 h-12 object-cover rounded-lg mr-3 shadow-card"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-button rounded-lg flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-secondaryText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-primaryText truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-secondaryText">
                              {(file.size / 1024 / 1024).toFixed(1)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="p-2 text-secondaryText hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Process button */}
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={startProcessing}
                  disabled={isUploading || isProcessing}
                  className="btn-primary w-full py-2.5 sm:py-3 text-sm sm:text-base"
                >
                  {isUploading ? 'Uploading...' : 
                   isProcessing ? 'Processing...' : 
                   `Generate Captions for ${selectedFiles.length} ${selectedFiles.length === 1 ? 'Image' : 'Images'}`}
                </button>
              </div>
            )}
            
            {/* Processing message */}
            {isProcessing && (
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-300">Processing your images...</p>
                    <p className="text-xs text-blue-300/70 mt-1">
                      Please be patient - this may take a few minutes depending on the number of images.
                      <br />
                      {processingProgress.current > 0 && (
                        <span className="mt-1 inline-block">
                          Progress: {processingProgress.current} of {processingProgress.total} images processed
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;